import { useCallback } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { AuthDependencyContainer } from '../../infrastructure/dependencies/AuthDependencyContainer';
import { LoginCredentials } from '../../domain/repositories/AuthRepository';
import { User } from '../../domain/entities/User';
import { orgPaths } from '@shared/routes/appPaths';

// Determina la ruta post-login según rol y slug
function getPostLoginRoute(user: User): string {
  if (user.role === 'superadmin') {
    return '/admin/organizations';
  }
  if (user.organizationSlug) {
    return orgPaths.myOrganization(user.organizationSlug);
  }
  return '/auth/login';
}

// Acepta solo paths internos seguros: empieza por '/', no por '//' (protocol-relative),
// sin esquema absoluto (http:/https:/javascript:). Fail-safe contra open-redirect (D6).
function isSafeInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !/^[a-z][a-z0-9+.-]*:/i.test(path);
}

// Resuelve el destino post-login con prioridad: redirect (query string) →
// state.from (deep-link en frío guardado por ProtectedRoute) → home por rol.
// El parámetro `search` es la query string de /auth/login (p.ej. '?redirect=...');
// URLSearchParams la decodifica automáticamente. Si el redirect no es un path interno
// seguro, se degrada silenciosamente al siguiente nivel (fail-safe, D6).
export function resolvePostLoginRoute(user: User, locationState: unknown, search: string): string {
  const redirect = new URLSearchParams(search).get('redirect');
  if (redirect && isSafeInternalPath(redirect)) {
    return redirect; // ya decodificado por URLSearchParams
  }
  const from = (locationState as { from?: Location } | null)?.from;
  if (from?.pathname) {
    return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
  }
  return getPostLoginRoute(user);
}

export const useAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, user, isLoading, setAuth, clearAuth, setLoading, jwtToken } = useAuthStore();

  const authRepository = AuthDependencyContainer.getAuthRepository();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setLoading(true);
      try {
        const { user, token } = await authRepository.login(credentials);
        setAuth(user, token);
        navigate(resolvePostLoginRoute(user, location.state, location.search), { replace: true });
      } catch (error: any) {
        setLoading(false);
        throw error;
      }
    },
    [authRepository, setAuth, setLoading, navigate, location.state, location.search]
  );

  const loginWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      const { user, token } = await authRepository.loginWithGoogle();
      setAuth(user, token);
      navigate(resolvePostLoginRoute(user, location.state, location.search), { replace: true });
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, [authRepository, setAuth, setLoading, navigate, location.state, location.search]);

  const loginWithMicrosoft = useCallback(async () => {
    setLoading(true);
    try {
      const { user, token } = await authRepository.loginWithMicrosoft();
      setAuth(user, token);
      navigate(resolvePostLoginRoute(user, location.state, location.search), { replace: true });
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, [authRepository, setAuth, setLoading, navigate, location.state, location.search]);

  const logout = useCallback(async () => {
    try {
      await authRepository.logout();
      clearAuth();
      navigate('/auth/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      clearAuth();
      navigate('/auth/login', { replace: true });
    }
  }, [authRepository, clearAuth, navigate]);

  const resetPassword = useCallback(
    async (email: string) => {
      setLoading(true);
      try {
        await authRepository.resetPassword(email);
      } finally {
        setLoading(false);
      }
    },
    [authRepository, setLoading]
  );

  const verifyPasswordResetCode = useCallback(
    async (oobCode: string): Promise<string> => {
      return authRepository.verifyPasswordResetCode(oobCode);
    },
    [authRepository]
  );

  const confirmPasswordReset = useCallback(
    async (oobCode: string, newPassword: string): Promise<void> => {
      setLoading(true);
      try {
        await authRepository.confirmPasswordReset(oobCode, newPassword);
      } finally {
        setLoading(false);
      }
    },
    [authRepository, setLoading]
  );

  const setPassword = useCallback(
    async (token: string, password: string): Promise<void> => {
      setLoading(true);
      try {
        await authRepository.setPassword(token, password);
      } finally {
        setLoading(false);
      }
    },
    [authRepository, setLoading]
  );

  const sendMagicLink = useCallback(
    async (email: string): Promise<void> => {
      await authRepository.sendMagicLink(email);
    },
    [authRepository]
  );

  const redeemMagicLink = useCallback(
    async (token: string): Promise<void> => {
      setLoading(true);
      try {
        const { user, token: jwt } = await authRepository.redeemMagicLink(token);
        setAuth(user, jwt);
        navigate(resolvePostLoginRoute(user, location.state, location.search), { replace: true });
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [authRepository, setAuth, setLoading, navigate, location.state, location.search]
  );

  return {
    isLoggedIn,
    user,
    isLoading,
    jwtToken,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    resetPassword,
    verifyPasswordResetCode,
    confirmPasswordReset,
    setPassword,
    sendMagicLink,
    redeemMagicLink,
  };
};
